import { $inject, $type } from './di-container.js';
import { LoggerService } from "../domain/logger.service.js";
import { debounce } from './functional-utils.js';
import { DomDetectorQuery } from './dom-detector-query.mjs';

/**
 * @typedef {object} _DomDetector_CheckEvents  optionally define additional events to detect
 * @property {boolean} [structureChange] if true, will look for tree changes underneath the dom element
 * @property {string[]|null} [attributeChange] if set to a list of attributes, it will also detect when such attributes change on the container element
 */

export class DomDetector {

  /** @type {HTMLElement} */
  _containerElement

  /** @type {_DomDetector_CheckEvents} */
  _checkEvents;

  /** @type {DomDetectorQuery[]} */
  _queries = [];

  /** @type {function[]} */
  _checks = [];

  /** @type {number} */
  _checkCount = 0;

  /**
   * @param {HTMLElement} [containerElement] the container element where to look - e.g. use document to detect all elements in page
   * @param {_DomDetector_CheckEvents} [checkEvents] what events to include in the dom search
   */
  constructor({ containerElement, checkEvents }) {
    this._containerElement = containerElement;
    this._checkEvents = checkEvents;
  }

  [$inject](obtain) {
    this._logger = obtain(LoggerService);
  }

  /**
   * @param {string[]} elementQuery
   * @param {function} callback
   * @returns {DomDetectorQuery}
   */
  addQuery(elementQuery, callback) {
    const query = new DomDetectorQuery(elementQuery, callback);
    this._queries.push(query);
    return query;
  }

  /**
   * @param {DomDetectorQuery} query
   */
  removeQuery(query) {
    this._queries.splice(this._queries.indexOf(query), 1);
  }

  /**
   * @param {function} cb
   */
  addCheck(cb) {
    this._checks.push(cb);
  }

  activate(skipInitialCheck = false) {
    if (!skipInitialCheck) {
      this._check();
    }
    this._startObserver();
  }

  deactivate() {
    this._queries = [];
    this._stopObserver();
  }

  checkNow() {
    this._doCheck();
  }

  _doCheck = () => {
    if (this._checkCount > 0) {
      this._checkCount += 1;
      return;
    }

    this._checkCount = 1;
    while (this._checkCount > 0) {
      for (let i=0; i<this._checks.length; i++) {
        this._checks[i]();
      }
      for (let i=0; i<this._queries.length; i++) {
        // find first element matching any query string
        const {elementQuery, callback} = this._queries[i];
        let element;
        for (let j=0; j<elementQuery.length; j++) {
          element = document.querySelector(elementQuery[j]);
          if (element) {
            break;
          }
        }
        callback(element);
      }
      this._checkCount -= 1;
    }
  };

  _check = debounce(this._doCheck, 100);

  _startObserver() {
    if (!window.MutationObserver) {
      this._logger.warn('global/missing-capability/mutation-observer', 'MutationObserver not available. Cannot detect dynamically-added/removed/changed elements.');
      return;
    }

    // on heavy dom changes, we only check after 100ms when no more mutations (for performance reasons)
    this._mutationObserver = new MutationObserver(this._check);

    const opts = {
      childList: !!this._checkEvents.structureChange,
      subtree: !!this._checkEvents.structureChange,
      attributes: false
    };

    if (this._checkEvents.attributeChange && this._checkEvents.attributeChange.length > 0) {
      opts.attributes = true;
      opts.attributeFilter = this._checkEvents.attributeChange;
    }

    this._mutationObserver.observe(this._containerElement, opts);
  }

  _stopObserver() {
    if (this._mutationObserver) {
      this._mutationObserver.disconnect();
      delete this._mutationObserver;
    }
  }

}

DomDetector[$type] = Symbol('DomDetector');
